import { faTag } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cryptoRandomString from "crypto-random-string";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { AlertOctagon, Printer } from "react-feather";
import toast from "react-hot-toast";
import Select from "react-select";

import inspectService from "../../../../../services/inspect/inspect.service";
import listingService from "../../../../../services/listingService";
import printService from "../../../../../services/print.service";

const BinStoreList = ({ vendor, setVendor, submitting, setSubmitting }) => {
  const [error, setError] = useState({});
  const [toggle, setToggle] = useState(true);

  const [inspectConditionOptions, setInspectConditionOptions] = useState();
  const [selectedInspectConditionOption, setSelectedInspectConditionOption] =
    useState();
  const [quantity, setQuantity] = useState(1);

  const [validationErrors, setValidationErrors] = useState({});
  const [listings, setListings] = useState([]);

  useEffect(() => {
    if (!inspectConditionOptions) {
      getInspectConditionOptions();
    }
  }, []);

  const getInspectConditionOptions = async () => {
    const conditions = await inspectService.getInspectOptions();

    if (conditions.length) {
      const optionizedConditions = conditions
        .map((condition) => {
          return {
            value: condition,
            label: condition.label,
          };
        })
        .filter((option) => Number(option.value.inspectConditionId) < 6);

      setInspectConditionOptions(optionizedConditions);
    } else {
      setError(conditions.message);
    }
  };

  const handleSubmit = async () => {
    setError({});
    setValidationErrors({});
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const userId = JSON.parse(localStorage.getItem("user")).userID;

    const lpns = [];
    for (let i = 0; i < quantity; i++) {
      // Create RPNs
      const token = cryptoRandomString({ length: 8, type: "distinguishable" });
      const lpn = `RPN${userId.toString().padStart(2, "0")}${token}`;

      lpns.push(lpn);
    }

    const response = await listingService.createAlternateListing({
      listingType: "BIN",
      inspectConditionId:
        selectedInspectConditionOption.value.inspectConditionId,
      lpns,
      userId,
      vendorId: vendor.vendorId,
    });

    if (response.success) {
      const listingIds = response.listings.map((listing) => listing.listingId);

      await printService.printBinStoreLabels({
        listingIds,
      });

      setListings(response.listings);
      setToggle(false);
      setSubmitting(false);
    } else {
      toast.error("Something went wrong, please try again.");
      setError(response);
      setSubmitting(false);
    }
  };

  const validateForm = () => {
    let valid = true;

    // Quantity validation --------------------------------
    if (!quantity) {
      setValidationErrors((prevState) => {
        return { ...prevState, quantity: "Quantity is required." };
      });
      valid = false;
    } else if (!/^\d+$/.test(quantity)) {
      setValidationErrors((prevState) => {
        return { ...prevState, quantity: "Quantity must be digits." };
      });
      valid = false;
    }

    return valid;
  };

  const resetState = () => {
    setSelectedInspectConditionOption();
    setError({});
    setQuantity(1);
    setListings([]);
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-start border-bottom">
        <FontAwesomeIcon icon={faTag} size="3x" className="me-3" />
        <div>
          <Card.Title>Bin Store List</Card.Title>
          <Card.Subtitle>
            Select a condition and enter quantity to create Bin Store listings
            for <strong>{vendor.vendorName}</strong>.
          </Card.Subtitle>
        </div>
      </Card.Header>
      <Card.Body>
        {Object.keys(error).length > 0 && (
          <Alert variant="danger" className="flex-fill alert-blink">
            <div className="alert-icon">
              <AlertOctagon
                className="position-relative top-50 start-50 translate-middle"
                size={20}
              />
            </div>
            <div className="alert-message font-weight-bold">
              {error.message}
            </div>
          </Alert>
        )}
        <Collapse in={toggle}>
          <div>
            <Form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>Condition</Form.Label>
                    <div className="react-select form-control p-0">
                      <Select
                        styles={{
                          container: (baseStyles) => ({
                            ...baseStyles,
                            margin: "-1px",
                          }),
                          control: (baseStyles) => ({
                            ...baseStyles,
                            borderRadius: "0.3rem",
                          }),
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder="Select a condition..."
                        onChange={(selectedOption) => {
                          setSelectedInspectConditionOption(selectedOption);
                        }}
                        value={selectedInspectConditionOption}
                        options={inspectConditionOptions}
                        isSearchable
                        isLoading={!inspectConditionOptions}
                        disabled={submitting}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={3}>
                  <Form.Group>
                    <Form.Label>Quantity</Form.Label>
                    <Form.Control
                      size="lg"
                      placeholder="Enter a quantity..."
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      onFocus={(e) => {
                        e.target.select();
                      }}
                      isInvalid={validationErrors.quantity}
                      disabled={submitting}
                    />
                    {validationErrors.quantity && (
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.quantity}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
                <Col xs="auto" className="align-self-start">
                  {/* Adds space above the button for validation errors */}
                  <div style={{ height: `${29.45}px` }}></div>
                  <Button
                    size="lg"
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={submitting || !selectedInspectConditionOption}
                  >
                    {submitting && (
                      <Spinner
                        className="me-2"
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </Col>
              </Row>
            </Form>
            <div className="text-center mt-3">
              <Card.Link
                onClick={() => {
                  if (!submitting) {
                    resetState();
                    setToggle(true);
                    setVendor({});
                  }
                }}
              >
                Select a different vendor
              </Card.Link>
            </div>
          </div>
        </Collapse>
        <Collapse in={!toggle}>
          <div>
            {listings.length > 0 && (
              <>
                <h4>Created Listings</h4>
                <Row className="my-3">
                  {listings.map((listing) => (
                    <Col key={listing.listingId} sm={6}>
                      <Alert variant="info">
                        <div className="alert-message font-weight-bold">
                          <div className="d-flex justify-content-between mb-2">
                            RPN: {listing.lpn}
                            <Button
                              className="m-0 px-1 py-0"
                              variant="outline-secondary"
                              title="Print RPN Label"
                              onClick={async () => {
                                await printService.printLpnLabel({
                                  lpn: listing.lpn,
                                });
                              }}
                            >
                              <Printer
                                style={{ width: "16px", height: "16px" }}
                              />{" "}
                              RPN
                            </Button>
                          </div>
                          <div className="d-flex justify-content-between">
                            Listing ID: {listing.listingId}
                            <Button
                              className="m-0 px-1 py-0"
                              variant="outline-secondary"
                              title="Print Bin Store Listing Label"
                              onClick={async () => {
                                await printService.printListingLabel({
                                  listingAccountId: 9,
                                  listingId: listing.listingId,
                                });
                              }}
                            >
                              <Printer
                                style={{ width: "16px", height: "16px" }}
                              />{" "}
                              Listing
                            </Button>
                          </div>
                        </div>
                      </Alert>
                    </Col>
                  ))}
                </Row>
                <Row className="mb-3">
                  <Col>
                    <h5 className="mb-0">Listing Account</h5>
                    <Card.Text>Bin Store</Card.Text>
                  </Col>
                  <Col>
                    <h5 className="mb-0">Condition</h5>
                    <Card.Text>
                      {selectedInspectConditionOption.label}
                    </Card.Text>
                  </Col>
                  <Col>
                    <h5 className="mb-0">Quantity</h5>
                    <Card.Text>{quantity}</Card.Text>
                  </Col>
                </Row>
              </>
            )}
            <div className="text-center">
              <Card.Link
                onClick={() => {
                  if (!submitting) {
                    resetState();
                    setToggle(true);
                  }
                }}
              >
                Create a new listing
              </Card.Link>
            </div>
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};

export default BinStoreList;
